import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../utils/api";

const AuthContext = createContext(null);

export function AuthProvider({ children, navigate }) {
  const [user,   setUser]   = useState(null);
  const [agents, setAgents] = useState([]);
  const [ready,  setReady]  = useState(false); // true once we've checked for an existing session

  // Restore session on load (page refresh / reopening the app) using the saved token.
  useEffect(() => {
    (async () => {
      if (api.getToken()) {
        try {
          const { user } = await api.request("/api/auth/me");
          setUser(user);
        } catch {
          api.setToken(null);
        }
      }
      setReady(true);
    })();
  }, []);

  const refreshAgents = useCallback(async () => {
    if (!user || user.role !== "admin") return;
    try { const { agents } = await api.request("/api/agents"); setAgents(agents); }
    catch { /* leave stale list in place rather than clearing it on a transient error */ }
  }, [user]);

  useEffect(() => { refreshAgents(); }, [refreshAgents]);

  const login = async (email, password) => {
    try {
      const { token, user } = await api.request("/api/auth/login", { method: "POST", body: { email, password }, auth: false });
      api.setToken(token);
      setUser(user);
      navigate(user.role === "admin" ? "dashboard" : "agent-dashboard");
      return null;
    } catch (e) { return e.message; }
  };

  const logout = () => { api.setToken(null); setUser(null); setAgents([]); navigate("login"); };

  const register = async (name, email, agency, password, kyc = {}) => {
    try { await api.request("/api/auth/register", { method: "POST", body: { name, email, agency, password, ...kyc }, auth: false }); return null; }
    catch (e) { return e.message; }
  };

  // Passwords are hashed server-side now, so this can't return the original password —
  // it issues (and returns) a fresh temporary one instead, same on-screen UX as before.
  const findUser = async (email) => {
    try { return await api.request("/api/auth/forgot-password", { method: "POST", body: { email }, auth: false }); }
    catch { return { found: false }; }
  };

  const addAgent = async (email, name, agency, password, role) => {
    await api.request("/api/agents", { method: "POST", body: { email, name, agency, password, role } });
    refreshAgents();
  };

  // Approve = activate the account, and (optionally) record how much the agent has
  // paid — shown back to them in their wallet as proof.
  const approveAgent = async (email, walletAmount = 0) => {
    await api.request(`/api/agents/${encodeURIComponent(email)}/approve`, { method: "POST", body: { wallet_amount: walletAmount } });
    refreshAgents();
  };

  const rejectAgent = async (email, reason = "") => {
    await api.request(`/api/agents/${encodeURIComponent(email)}/reject`, { method: "POST", body: { reason } });
    refreshAgents();
  };

  // Set (or zero out, once the agent's work is complete) their wallet balance.
  const setWallet = async (email, amount) => {
    await api.request(`/api/agents/${encodeURIComponent(email)}/wallet`, { method: "POST", body: { amount } });
    refreshAgents();
  };
  const zeroWallet = (email) => setWallet(email, 0);

  const deleteAgent = async (email) => {
    await api.request(`/api/agents/${encodeURIComponent(email)}`, { method: "DELETE" });
    refreshAgents();
  };

  const changePass = async (email, password) => {
    await api.request(`/api/agents/${encodeURIComponent(email)}/password`, { method: "POST", body: { password } });
  };

  const getAgents = () => agents; // kept as a function for compatibility with existing pages

  const refreshMe = async () => {
    if (!api.getToken()) return;
    try { const { user } = await api.request("/api/auth/me"); setUser(user); } catch {}
  };

  return (
    <AuthContext.Provider value={{
      user, users: agents, ready,
      login, logout, register, findUser,
      addAgent, approveAgent, rejectAgent, setWallet, zeroWallet, deleteAgent, changePass,
      getAgents, refreshAgents, refreshMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
